<template>
    <div class="ui-range">
        <div class="ui-range__header">
            <span class="ui-range__label">{{ label }}</span>

            <span class="ui-range__value">
                <template v-if="rangeMode && Array.isArray(modelValue)">
                    {{ formatValue(modelValue?.[0]) }} —
                    {{ formatValue(modelValue?.[1]) }}
                </template>
                <template v-else>
                    {{ formatValue(modelValue as number) }}
                </template>
            </span>
        </div>

        <div ref="range" class="ui-range__slider"></div>
    </div>
</template>

<script lang="ts" setup>
    import 'nouislider/dist/nouislider.css';

    import { defineProps, defineEmits, watch, onMounted, ref, PropType } from 'vue';
    import noUiSlider from 'nouislider';

    const props = defineProps({
        modelValue: {
            type: [Number, Array] as PropType<number | number[]>,
            required: true,
        },
        rangeMode: {
            type: Boolean,
            default: true,
        },
        min: {
            type: Number,
            default: 0,
        },
        max: {
            type: Number,
            default: 100,
        },
        interval: {
            type: Number,
            default: 1,
        },
        label: {
            type: String,
            default: 'Заголовок',
        },
        unit: {
            type: String,
            default: '',
        },
        formatNumbers: {
            type: Boolean,
            default: true,
        },
    });

    const emit = defineEmits(['update:modelValue']);

    const range = ref<HTMLDivElement | null>(null);

    watch(
        () => props.modelValue,
        (newValue) => {
            if (range.value?.noUiSlider) {
                if (props.rangeMode) {
                    const safeVal = Array.isArray(newValue) ? newValue : [0, 0];
                    range.value.noUiSlider.set(safeVal);
                }
                else {
                    range.value.noUiSlider.set([Number(newValue) || 0]);
                }
            }
        },
        { immediate: true },
    );

    function formatValue(value: number | undefined): string {
        if (value === undefined) {
            return '—';
        }

        const formattedNumber = props.formatNumbers ? value.toLocaleString() : value.toString();

        return props.unit && value !== 0 ? `${formattedNumber} ${props.unit}` : formattedNumber;
    }

    function updateValue(values: string[]): void {
        const parsedValues = values.map((v) => {
            if (!v) {
                return 0;
            }

            return Number(v);
        });

        if (props.rangeMode) {
            // Режим диапазона
            if (
                Array.isArray(props.modelValue)
                && (parsedValues[0] !== props.modelValue[0] || parsedValues[1] !== props.modelValue[1])
            ) {
                emit('update:modelValue', parsedValues);
            }
        }
        else {
            // Режим единичного значения
            const newValue = parsedValues[0];
            if (newValue !== props.modelValue) {
                emit('update:modelValue', newValue);
            }
        }
    }

    onMounted(() => {
        if (range.value) {
            noUiSlider.create(range.value, {
                start: props.rangeMode
                    ? (Array.isArray(props.modelValue) ? props.modelValue : [props.min, props.max])
                    : [Number(props.modelValue) || 0],
                connect: props.rangeMode ? true : [true, false],
                range: {
                    min: props.min,
                    max: props.max,
                },
                step: props.interval,
                animate: false,
                animationDuration: 0,
            });

            range.value.noUiSlider?.on('update', updateValue);
        }
    });
</script>

<style lang="scss">
    @use './UIRange.scss' as *;

    .noUi-target,
    .noUi-target * {
        transition: none !important;
    }
</style>
